if (!customElements.get('cart-notification')) {
  customElements.define(
    'cart-notification',
    class CartNotification extends HTMLElement {
      constructor() {
        super();

        this.notification =
          document.getElementById(
            'cart-notification'
          ) || this;

        this.header =
          document.querySelector(
            'sticky-header'
          );

        this.activeElement = null;
        this.cartItemKey = null;

        this.focusTrapTimeout = null;
        this.transitionEndHandler = null;
        this.openRequestId = 0;

        this.onBodyClick =
          this.handleBodyClick.bind(this);

        this.onKeyup =
          this.handleKeyup.bind(this);

        this.onButtonClick =
          this.handleButtonClick.bind(this);

        this.notification.addEventListener(
          'keyup',
          this.onKeyup
        );

        /*
         * Delegated listener survives Shopify
         * section HTML replacements.
         */
        this.addEventListener(
          'click',
          this.onButtonClick
        );
      }

      disconnectedCallback() {
        document.body.removeEventListener(
          'click',
          this.onBodyClick
        );

        this.notification?.removeEventListener(
          'keyup',
          this.onKeyup
        );

        this.removeEventListener(
          'click',
          this.onButtonClick
        );

        this.clearFocusHandlers();
      }

      handleKeyup(event) {
        if (
          event.code === 'Escape' ||
          event.key === 'Escape'
        ) {
          this.close();
        }
      }

      handleButtonClick(event) {
        const target =
          event.target instanceof Element
            ? event.target
            : event.target?.parentElement;

        if (!target) {
          return;
        }

        const closeButton =
          target.closest(
            'button[type="button"]'
          );

        if (
          !closeButton ||
          !this.contains(closeButton)
        ) {
          return;
        }

        this.close();
      }

      open() {
        if (!this.notification) {
          return;
        }

        const currentOpenRequest =
          ++this.openRequestId;

        this.clearFocusHandlers();

        this.notification.classList.add(
          'animate',
          'active'
        );

        /*
         * Prevent duplicate body listeners.
         */
        document.body.removeEventListener(
          'click',
          this.onBodyClick
        );

        document.body.addEventListener(
          'click',
          this.onBodyClick
        );

        let focusHandled = false;

        const handleFocus = () => {
          if (
            focusHandled ||
            currentOpenRequest !==
              this.openRequestId ||
            !this.notification.classList.contains(
              'active'
            )
          ) {
            return;
          }

          focusHandled = true;

          this.clearFocusHandlers();

          this.notification.focus?.();

          if (
            typeof trapFocus ===
            'function'
          ) {
            trapFocus(
              this.notification
            );
          }
        };

        /*
         * Prefer the CSS transition finishing.
         */
        this.transitionEndHandler =
          (event) => {
            if (
              event.target !==
              this.notification
            ) {
              return;
            }

            handleFocus();
          };

        this.notification.addEventListener(
          'transitionend',
          this.transitionEndHandler,
          {
            once: true,
          }
        );

        /*
         * Fallback:
         * transitionend may never fire if animations
         * are disabled or interrupted.
         */
        this.focusTrapTimeout =
          setTimeout(
            handleFocus,
            500
          );
      }

      close() {
        /*
         * Invalidates any pending open/focus task.
         */
        this.openRequestId += 1;

        this.clearFocusHandlers();

        this.notification?.classList.remove(
          'active'
        );

        document.body.removeEventListener(
          'click',
          this.onBodyClick
        );

        if (
          typeof removeTrapFocus ===
          'function'
        ) {
          removeTrapFocus(
            this.activeElement
          );
        } else {
          this.activeElement?.focus?.();
        }
      }

      clearFocusHandlers() {
        if (this.focusTrapTimeout) {
          clearTimeout(
            this.focusTrapTimeout
          );

          this.focusTrapTimeout =
            null;
        }

        if (
          this.transitionEndHandler &&
          this.notification
        ) {
          this.notification.removeEventListener(
            'transitionend',
            this.transitionEndHandler
          );

          this.transitionEndHandler =
            null;
        }
      }

      renderContents(parsedState) {
        if (!parsedState) {
          console.error(
            '[Cart Notification] Invalid cart response.'
          );

          return;
        }

        this.cartItemKey =
          parsedState.key || null;

        if (!parsedState.sections) {
          console.error(
            '[Cart Notification] Shopify sections were not returned.'
          );

          return;
        }

        this.getSectionsToRender().forEach(
          (section) => {
            const target =
              document.getElementById(
                section.id
              );

            if (!target) {
              return;
            }

            const sectionHTML =
              parsedState.sections[
                section.id
              ];

            if (!sectionHTML) {
              return;
            }

            const innerHTML =
              this.getSectionInnerHTML(
                sectionHTML,
                section.selector
              );

            if (innerHTML === null) {
              console.warn(
                `[Cart Notification] Could not render section: ${section.id}`
              );

              return;
            }

            target.innerHTML =
              innerHTML;
          }
        );

        if (
          this.header &&
          typeof this.header.reveal ===
            'function'
        ) {
          this.header.reveal();
        }

        this.open();
      }

      getSectionsToRender() {
        return [
          {
            id:
              'cart-notification-product',

            selector:
              this.cartItemKey
                ? `[id="cart-notification-product-${CSS.escape(
                    this.cartItemKey
                  )}"]`
                : null,
          },

          {
            id:
              'cart-notification-button',
          },

          {
            id:
              'cart-icon-bubble',
          },
        ];
      }

      getSectionInnerHTML(
        html,
        selector =
          '.shopify-section'
      ) {
        if (!html) {
          return null;
        }

        const parsedHTML =
          new DOMParser().parseFromString(
            html,
            'text/html'
          );

        /*
         * cart-notification-product needs its
         * specific line-item selector after the
         * Add to Cart response gives us the key.
         */
        if (!selector) {
          return (
            parsedHTML.body
              ?.innerHTML ?? null
          );
        }

        const source =
          parsedHTML.querySelector(
            selector
          );

        return source
          ? source.innerHTML
          : null;
      }

      handleBodyClick(event) {
        if (!this.notification) {
          return;
        }

        const target =
          event.target instanceof Element
            ? event.target
            : event.target?.parentElement;

        if (!target) {
          return;
        }

        /*
         * Clicking inside the notification
         * should not close it.
         */
        if (
          this.notification === target ||
          this.notification.contains(
            target
          )
        ) {
          return;
        }

        /*
         * Preserve focus restoration behavior
         * when the click came from a header
         * disclosure/menu.
         */
        const disclosure =
          target.closest(
            'details-disclosure, header-menu'
          );

        this.activeElement =
          disclosure?.querySelector(
            'summary'
          ) ||
          this.activeElement ||
          null;

        this.close();
      }

      setActiveElement(element) {
        if (
          element instanceof HTMLElement
        ) {
          this.activeElement =
            element;
        }
      }
    }
  );
}